/**
 * PATH: packages/email-hub/src/feature/lib/threadColors.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-THREADCOLORS-001
 * PURPOSE: Stable, accessible per-message colour accents for the stacked
 *          thread reader. Each message in a conversation gets one colour
 *          (assigned by its position in the thread); that colour ties the
 *          message block, its header dot, and its group in the sticky
 *          bottom attachment bar together so an operator can see at a glance
 *          which attachment came from which message.
 * WHY:     The operator asked for "sectionalised colour codes" linking
 *          attachments back to the part of the conversation they belong to.
 *          Colour-by-message is the only mapping that's unambiguous (inbound
 *          attachments are attached at the message level, not to body
 *          sub-sections).
 * NOTE:    Every class string is a COMPLETE literal so Tailwind's JIT (which
 *          scans `packages/email-hub/src/**`) generates it. Do not build
 *          these strings dynamically.
 */

export interface ThreadColor {
  /** Stable identity key (also handy for React keys / tests). */
  name: string
  /** Left-border accent on the message block (`border-l-4` lives on the el). */
  border: string
  /** Solid dot used in the message header + the attachment-bar group label. */
  dot: string
  /** Subtle tinted background strip behind the message header. */
  headerBg: string
  /** Text token for the coloured sender/label text. */
  text: string
  /** Ring used to highlight the group currently scrolled into view. */
  ring: string
  /** Soft background for the attachment-bar group container. */
  chipBg: string
}

/**
 * Fixed palette — chosen for distinguishability in BOTH light and dark mode.
 * Order is stable: message N always gets `PALETTE[N % PALETTE.length]`.
 */
export const THREAD_COLOR_PALETTE: ThreadColor[] = [
  {
    name: 'sky',
    border: 'border-l-sky-400 dark:border-l-sky-500',
    dot: 'bg-sky-500',
    headerBg: 'bg-sky-50/70 dark:bg-sky-950/30',
    text: 'text-sky-700 dark:text-sky-300',
    ring: 'ring-sky-400/60',
    chipBg: 'bg-sky-100/60 dark:bg-sky-900/30',
  },
  {
    name: 'emerald',
    border: 'border-l-emerald-400 dark:border-l-emerald-500',
    dot: 'bg-emerald-500',
    headerBg: 'bg-emerald-50/70 dark:bg-emerald-950/30',
    text: 'text-emerald-700 dark:text-emerald-300',
    ring: 'ring-emerald-400/60',
    chipBg: 'bg-emerald-100/60 dark:bg-emerald-900/30',
  },
  {
    name: 'violet',
    border: 'border-l-violet-400 dark:border-l-violet-500',
    dot: 'bg-violet-500',
    headerBg: 'bg-violet-50/70 dark:bg-violet-950/30',
    text: 'text-violet-700 dark:text-violet-300',
    ring: 'ring-violet-400/60',
    chipBg: 'bg-violet-100/60 dark:bg-violet-900/30',
  },
  {
    name: 'amber',
    border: 'border-l-amber-400 dark:border-l-amber-500',
    dot: 'bg-amber-500',
    headerBg: 'bg-amber-50/70 dark:bg-amber-950/30',
    text: 'text-amber-700 dark:text-amber-300',
    ring: 'ring-amber-400/60',
    chipBg: 'bg-amber-100/60 dark:bg-amber-900/30',
  },
  {
    name: 'rose',
    border: 'border-l-rose-400 dark:border-l-rose-500',
    dot: 'bg-rose-500',
    headerBg: 'bg-rose-50/70 dark:bg-rose-950/30',
    text: 'text-rose-700 dark:text-rose-300',
    ring: 'ring-rose-400/60',
    chipBg: 'bg-rose-100/60 dark:bg-rose-900/30',
  },
  {
    name: 'cyan',
    border: 'border-l-cyan-400 dark:border-l-cyan-500',
    dot: 'bg-cyan-500',
    headerBg: 'bg-cyan-50/70 dark:bg-cyan-950/30',
    text: 'text-cyan-700 dark:text-cyan-300',
    ring: 'ring-cyan-400/60',
    chipBg: 'bg-cyan-100/60 dark:bg-cyan-900/30',
  },
  {
    name: 'lime',
    border: 'border-l-lime-400 dark:border-l-lime-500',
    dot: 'bg-lime-500',
    headerBg: 'bg-lime-50/70 dark:bg-lime-950/30',
    text: 'text-lime-700 dark:text-lime-300',
    ring: 'ring-lime-400/60',
    chipBg: 'bg-lime-100/60 dark:bg-lime-900/30',
  },
  {
    name: 'fuchsia',
    border: 'border-l-fuchsia-400 dark:border-l-fuchsia-500',
    dot: 'bg-fuchsia-500',
    headerBg: 'bg-fuchsia-50/70 dark:bg-fuchsia-950/30',
    text: 'text-fuchsia-700 dark:text-fuchsia-300',
    ring: 'ring-fuchsia-400/60',
    chipBg: 'bg-fuchsia-100/60 dark:bg-fuchsia-900/30',
  },
]

/**
 * Neutral colour for the single-message fallback (no thread to colour-code).
 * Uses the semantic muted/border tokens so it reads as "no accent".
 */
export const NEUTRAL_THREAD_COLOR: ThreadColor = {
  name: 'neutral',
  border: 'border-l-border',
  dot: 'bg-muted-foreground',
  headerBg: 'bg-muted/40',
  text: 'text-muted-foreground',
  ring: 'ring-border',
  chipBg: 'bg-muted/60',
}

/**
 * Colour for message at position `index` in the thread (0-based). Stable and
 * wraps around the palette for very long threads. Negative / non-finite
 * indices clamp to the first colour.
 */
export function colorForIndex(index: number): ThreadColor {
  if (!Number.isFinite(index) || index < 0) return THREAD_COLOR_PALETTE[0]
  return THREAD_COLOR_PALETTE[Math.floor(index) % THREAD_COLOR_PALETTE.length]
}
